import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../../styles/FormPages.css";
import DocumentUpload from "../../components/DocumentUpload";
import LocationSelectors from "../../components/LocationSelectors";
import { states, districts } from "../../data/locationData";

const PoliceFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { country, state: stateVal, district, lawCategory } = location.state || {};

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  const [formData, setFormData] = useState({
    country: country || "",
    state: stateVal || "",
    district: district || "",

    complainantFullName: "",
    complainantFatherOrHusband: "",
    complainantAge: "",
    complainantGender: "",
    complainantOccupation: "",
    complainantContact: "",
    complainantEmail: "",
    complainantAddress: "",
    accusedFullName: "",
    accusedRelationship: "",
    accusedRelationshipOther: "",
    accusedAddress: "",
    accusedRole: "",
    incidentTitle: "",
    incidentTitleOther: "",
    incidentDescription: "",
    incidentDate: "",
    incidentTime: "",
    incidentLocation: "",
    witnessDetails: "",
    evidenceDetails: "",
    policeStation: "",
    policeDistrict: district || "",
    policeState: stateVal || "",
    isUrgent: "No",
    previousComplaint: "No",
    declaration: false,
  });

  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [extractedContent, setExtractedContent] = useState({});

  const handleDocumentsChange = (files, content) => {
    setUploadedDocuments(files);
    setExtractedContent(content);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handlePoliceStateSelect = (e) => {
    const nextState = e.target.value;
    setFormData((prev) => ({
      ...prev,
      policeState: nextState,
      policeDistrict: "",
    }));
  };

  const handlePoliceDistrictSelect = (e) => {
    setFormData((prev) => ({
      ...prev,
      policeDistrict: e.target.value,
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const selectedCountry = formData.country || country || "";
    const selectedState = formData.state || stateVal || "";
    const selectedDistrict = formData.district || district || "";
    
    let detailsText = `
👤 Complainant Details:
Full Name: ${formData.complainantFullName}
Father's/Husband's Name: ${formData.complainantFatherOrHusband}
Age: ${formData.complainantAge}
Gender: ${formData.complainantGender}
Occupation: ${formData.complainantOccupation}
Contact: ${formData.complainantContact}
Email: ${formData.complainantEmail}
Address: ${formData.complainantAddress}

👥 Accused Details:
Full Name: ${formData.accusedFullName}
Relationship: ${formData.accusedRelationshipOther || formData.accusedRelationship}
Address: ${formData.accusedAddress}
Role in Incident: ${formData.accusedRole}

📋 Incident Details:
Incident Title: ${formData.incidentTitleOther || formData.incidentTitle}
Description: ${formData.incidentDescription}
Date: ${formData.incidentDate}
Time: ${formData.incidentTime}
Location: ${formData.incidentLocation}

👁️ Evidence & Witnesses:
Witness Details: ${formData.witnessDetails}
Evidence Details: ${formData.evidenceDetails}

🚔 Police Station Details:
Police Station: ${formData.policeStation}
District: ${formData.policeDistrict}
State: ${formData.policeState}
Is Urgent: ${formData.isUrgent}
Previous Complaint: ${formData.previousComplaint}
`.trim();

    // Add extracted document content if available
    if (uploadedDocuments.length > 0) {
      detailsText += `\n\n📄 Supporting Documents:\n`;
      uploadedDocuments.forEach((doc, index) => {
        detailsText += `\nDocument ${index + 1}: ${doc.name}`;
        if (extractedContent[doc.id]) {
          detailsText += `\nExtracted Content:\n${extractedContent[doc.id]}\n`;
        }
      });
    }

    navigate("/drafts", {
      state: {
        query: detailsText,
        summary: detailsText,
        country: selectedCountry,
        state: selectedState,
        district: selectedDistrict,
        lawCategory,
        draftType: "police",
        includeSections: ["police"],
        entityName: formData.complainantFullName,
        uploadedDocuments,
        extractedContent,
      },
    });
  };

  const renderStep1 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👤 Complainant Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Full Name *</label>
          <input
            type="text"
            name="complainantFullName"
            value={formData.complainantFullName}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Father's/Husband's Name *</label>
          <input
            type="text"
            name="complainantFatherOrHusband"
            value={formData.complainantFatherOrHusband}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Age *</label>
          <input
            type="number"
            name="complainantAge"
            value={formData.complainantAge}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Gender *</label>
          <select
            name="complainantGender"
            value={formData.complainantGender}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Occupation *</label>
          <input
            type="text"
            name="complainantOccupation"
            value={formData.complainantOccupation}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Contact Number *</label>
          <input
            type="tel"
            name="complainantContact"
            value={formData.complainantContact}
            onChange={handleInputChange}
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Email ID</label>
          <input
            type="email"
            name="complainantEmail"
            value={formData.complainantEmail}
            onChange={handleInputChange}
          />
        </div>
        <div className="form-page-col">
          <label>Address *</label>
          <textarea
            name="complainantAddress"
            value={formData.complainantAddress}
            onChange={handleInputChange}
            rows={2}
            required
          />
        </div>
      </div>

      <LocationSelectors
        country={formData.country}
        state={formData.state}
        district={formData.district}
        onChange={(next) => setFormData((prev) => ({ ...prev, ...next }))}
      />
    </div>
  );

  const renderStep2 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        👥 Accused Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Accused Full Name *</label>
          <input
            type="text"
            name="accusedFullName"
            value={formData.accusedFullName}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Relationship to Complainant *</label>
          <select
            name="accusedRelationship"
            value={formData.accusedRelationship}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Relationship</option>
            <option value="Family Member">Family Member</option>
            <option value="Spouse">Spouse</option>
            <option value="Relative">Relative</option>
            <option value="Neighbour">Neighbour</option>
            <option value="Friend">Friend</option>
            <option value="Colleague">Colleague</option>
            <option value="Business Partner">Business Partner</option>
            <option value="Employee">Employee</option>
            <option value="Employer">Employer</option>
            <option value="Tenant">Tenant</option>
            <option value="Landlord">Landlord</option>
            <option value="Stranger">Stranger</option>
            <option value="Acquaintance">Acquaintance</option>
            <option value="Other">Other (specify)</option>
          </select>
        </div>
      </div>
      {formData.accusedRelationship === "Other" && (
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Specify Relationship *</label>
            <input
              type="text"
              name="accusedRelationshipOther"
              value={formData.accusedRelationshipOther}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>
      )}
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Accused Address *</label>
          <textarea
            name="accusedAddress"
            value={formData.accusedAddress}
            onChange={handleInputChange}
            rows={3}
            placeholder="Complete address of the accused person"
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Role in Incident *</label>
          <select
            name="accusedRole"
            value={formData.accusedRole}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Role</option>
            <option value="Primary Accused">Primary Accused</option>
            <option value="Co-Accused">Co-Accused</option>
            <option value="Accomplice">Accomplice</option>
            <option value="Instigator">Instigator</option>
            <option value="Abettor">Abettor</option>
            <option value="Conspirator">Conspirator</option>
            <option value="Unknown Role">Unknown Role</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="form-page-section">
      <div className="form-page-section-title">
        📋 Incident Details
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Incident Type *</label>
          <select
            name="incidentTitle"
            value={formData.incidentTitle}
            onChange={handleInputChange}
            required
          >
            <option value="">Select Incident Type</option>
            <option value="Theft">Theft</option>
            <option value="Burglary">Burglary</option>
            <option value="Robbery">Robbery</option>
            <option value="Fraud">Fraud</option>
            <option value="Cheating">Cheating</option>
            <option value="Assault">Assault</option>
            <option value="Harassment">Harassment</option>
            <option value="Domestic Violence">Domestic Violence</option>
            <option value="Cybercrime">Cybercrime</option>
            <option value="Property Dispute">Property Dispute</option>
            <option value="Threat">Threat</option>
            <option value="Defamation">Defamation</option>
            <option value="Missing Person">Missing Person</option>
            <option value="Accident">Accident</option>
            <option value="Other">Other (specify)</option>
          </select>
        </div>
        <div className="form-page-col">
          {formData.incidentTitle === "Other" && (
            <>
              <label>Specify Incident Type *</label>
              <input
                type="text"
                name="incidentTitleOther"
                value={formData.incidentTitleOther}
                onChange={handleInputChange}
                required
              />
            </>
          )}
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col">
          <label>Incident Date *</label>
          <input
            type="date"
            name="incidentDate"
            value={formData.incidentDate}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="form-page-col">
          <label>Incident Time</label>
          <input
            type="time"
            name="incidentTime"
            value={formData.incidentTime}
            onChange={handleInputChange}
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Incident Location *</label>
          <textarea
            name="incidentLocation"
            value={formData.incidentLocation}
            onChange={handleInputChange}
            rows={3}
            placeholder="Exact location where the incident occurred"
            required
          />
        </div>
      </div>
      <div className="form-page-row">
        <div className="form-page-col full-width">
          <label>Detailed Description of Incident *</label>
          <textarea
            name="incidentDescription"
            value={formData.incidentDescription}
            onChange={handleInputChange}
            rows={5}
            placeholder="Provide a detailed description of what happened, sequence of events, etc."
            required
          />
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <>
      <div className="form-page-section">
        <div className="form-page-section-title">
          🚔 Police Station & Additional Details
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Police Station Name *</label>
            <input
              type="text"
              name="policeStation"
              value={formData.policeStation}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-page-col">
            <label>Police District *</label>
            {(() => {
              const hasStateList = (states[formData.country] || []).length > 0;
              const policeDistrictOptions = districts[formData.policeState] || [];

              if (!hasStateList) {
                return (
                  <input
                    type="text"
                    name="policeDistrict"
                    value={formData.policeDistrict}
                    onChange={handleInputChange}
                    required
                  />
                );
              }

              if (formData.policeState && policeDistrictOptions.length === 0) {
                return (
                  <input
                    type="text"
                    name="policeDistrict"
                    value={formData.policeDistrict}
                    onChange={handleInputChange}
                    required
                  />
                );
              }

              return (
                <select
                  name="policeDistrict"
                  value={formData.policeDistrict}
                  onChange={handlePoliceDistrictSelect}
                  required
                  disabled={!formData.policeState}
                >
                  <option value="">Select District</option>
                  {policeDistrictOptions.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              );
            })()}
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Police State *</label>
            {(states[formData.country] || []).length > 0 ? (
              <select
                name="policeState"
                value={formData.policeState}
                onChange={handlePoliceStateSelect}
                required
                disabled={!formData.country}
              >
                <option value="">Select State</option>
                {(states[formData.country] || []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                name="policeState"
                value={formData.policeState}
                onChange={handleInputChange}
                required
              />
            )}
          </div>
          <div className="form-page-col">
            <label>Is this Urgent? *</label>
            <select
              name="isUrgent"
              value={formData.isUrgent}
              onChange={handleInputChange}
              required
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Previous Complaint Filed? *</label>
            <select
              name="previousComplaint"
              value={formData.previousComplaint}
              onChange={handleInputChange}
              required
            >
              <option value="No">No</option>
              <option value="Yes">Yes</option>
            </select>
          </div>
        </div>
        <div className="form-page-row">
          <div className="form-page-col">
            <label>Witness Details</label>
            <textarea
              name="witnessDetails"
              value={formData.witnessDetails}
              onChange={handleInputChange}
              rows={3}
              placeholder="Names, contact details, and statements of witnesses (if any)"
            />
          </div>
          <div className="form-page-col">
            <label>Evidence Details</label>
            <textarea
              name="evidenceDetails"
              value={formData.evidenceDetails}
              onChange={handleInputChange}
              rows={3}
              placeholder="Details of physical evidence, documents, photos, etc."
            />
          </div>
        </div>
      </div>

      <div className="form-page-section">
        <div className="form-page-section-title">
          📄 Supporting Documents
        </div>
        <p className="form-page-note">
          Upload relevant documents such as receipts, photos, messages, emails, 
          contracts, or any other evidence related to your complaint. The system 
          will extract text from these documents to enhance your legal draft.
        </p>
        <DocumentUpload 
          onDocumentsChange={handleDocumentsChange}
          maxFiles={8}
          acceptedTypes=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
        />
      </div>

      <div className="form-page-declaration">
        <div className="form-page-section-title">
          ✅ Declaration
        </div>
        <label>
          <input
            type="checkbox"
            name="declaration"
            checked={formData.declaration}
            onChange={handleInputChange}
            required
          />
          <span>I declare that all the information provided is true and I understand the consequences of false complaint *</span>
        </label>
      </div>
    </>
  );

  return (
    <div className="form-page-container">
      <div className="form-page-header">
        <a href="#" onClick={() => navigate(-1)} className="form-page-back-link">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Draft Selection
        </a>
        <h1 className="form-page-title">File Police Station Complaint</h1>
        <p className="form-page-subtitle">Fill in the details below to generate a professional police complaint</p>
      </div>

      <div className="form-page-content">
        <form onSubmit={handleSubmit}>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}

          <div className="form-page-navigation">
            <div className="form-page-step-indicator">
              Step {currentStep} of {totalSteps}
            </div>
            <div className="form-page-buttons">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="form-page-btn form-page-btn-secondary"
                >
                  Previous
                </button>
              )}
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="form-page-btn form-page-btn-primary"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  className="form-page-btn form-page-btn-primary"
                >
                  Generate Police Complaint
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PoliceFormPage;
